import React, { useCallback, useEffect, useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { MAX_CONVERSIONS } from '@bingads-webui-campaign-react/bid-strategy-component';
import {
  Button, Label, Radio, RadioGroup, Text,
  makeStyles, mergeClasses, shorthands,
} from '@fluentui/react-components';
import { Edit16Regular } from '@fluentui/react-icons';
import { TagGroup, Tag } from '@fluentui/react-tags';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { CustomerAcquisitionSetup } from './side-panel/customer-acquisition-setup';
import { NCA_STRATEGY_OPTIONS } from './constants';
import { AccountLevelRequiredWarning, BiddingStrategyWarning } from './message-bars';

const useNewCustomerAcquisitionStyle = makeStyles({
  radioGroupContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    '& input[type="radio"]': {
      height: '20px',
      width: '20px',
      ...shorthands.margin('0px'),
    },
    '& .fui-Radio__indicator': {
      height: '20px',
      width: '20px',
      ...shorthands.margin('0px'),
    },
    '& label': {
      ...shorthands.padding('0px', '0px', '0px', '8px'),
    },
  },
  radioOption: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    '& .edit-button': {
      width: 'fit-content',
      ...shorthands.padding('4px', '6px'),
    },
  },
  additionalConversionValueContainer: {
    display: 'flex',
    flexDirection: 'column',
    '& .info-row': {
      display: 'flex',
      flexDirection: 'row',
      ...shorthands.gap('8px'),
      alignItems: 'center',
    },
  },
  existingAudiencesContainer: {
    display: 'flex',
    flexDirection: 'column',
    '& .info-row': {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      ...shorthands.gap('6px'),
      marginTop: '2px',
      alignItems: 'center',
    },
  },
  messageBar: {
    '& .fui-MessageBar__icon': {
      marginRight: '12px',
    },
    '& .fui-MessageBarBody': {
      whiteSpace: 'normal',
      paddingTop: '8px',
      paddingBottom: '8px',
      fontSize: '12px',
    },
  },
});

export const NewCustomerAcquisitionImpl = (props) => {
  const {
    newCustomerAcquisition, cid, aid, bidStrategy, optOut, i18n, onChange,
  } = props;
  const serverContext = useContext(ServerContext);
  const { CurrentAccount: { CurrencyCode } } = serverContext || {};
  const { campaignSettings, accountSettings } = newCustomerAcquisition || {};
  const { bidOnlyMode } = campaignSettings || {};

  const styles = useNewCustomerAcquisitionStyle();

  const [isSetupEditorOpen, setIsSetupEditorOpen] = useState(false);
  const onOpenSetupEditor = useCallback(() => setIsSetupEditorOpen(true), []);
  const onCloseSetupEditor = useCallback(() => setIsSetupEditorOpen(false), []);
  const hasValidationError = useMemo(() => !optOut && bidStrategy === MAX_CONVERSIONS.type && !bidOnlyMode, [optOut, bidStrategy, bidOnlyMode]);
  const onRadioChange = useCallback((_, data) => {
    onChange({
      newCustomerAcquisition: {
        ...newCustomerAcquisition,
        campaignSettings: {
          ...campaignSettings,
          bidOnlyMode: data.value === NCA_STRATEGY_OPTIONS.BID_ONLY,
        },
      },
    });
  }, [campaignSettings, newCustomerAcquisition, onChange]);

  const renderSettings = useCallback(() => (
    <>
      { !bidOnlyMode && accountSettings && accountSettings.additionalConversionValue &&
        (
          <div className={styles.additionalConversionValueContainer}>
            <Text size={300} weight="regular">
              {i18n.getString(_TL_('Conversion value for new customers'))}
            </Text>
            <div className="info-row">
              { campaignSettings && campaignSettings.additionalConversionValue !== null
                ? (
                  <Text className="additional-value-preview" size={300} weight="bold">{i18n.formatCurrency(Number(campaignSettings.additionalConversionValue.toFixed(2)), CurrencyCode)}</Text>
                )
                : (
                  <>
                    <Text className="additional-value-preview" size={300} weight="bold">{i18n.formatCurrency(Number(accountSettings.additionalConversionValue.toFixed(2)), CurrencyCode)}</Text>
                    <Text size={200} weight="regular">{i18n.getString(_TL_('(Account-level setting)'))}</Text>
                  </>
                )
              }
              <Button
                className={mergeClasses('edit-button', 'nca-edit')}
                icon={<Edit16Regular />}
                appearance="transparent"
                size="small"
                onClick={onOpenSetupEditor}
                disabled={optOut}
              />
            </div>
          </div>
        )
      }
      { accountSettings && accountSettings.audiences && accountSettings.audiences.length > 0 &&
        (
          <div className={styles.existingAudiencesContainer}>
            <Text size={300} weight="regular">
              {i18n.getString(_TL_('Existing audience lists'))}
            </Text>
            <TagGroup className="info-row" role="list">
              { accountSettings.audiences.map(audience => (
                <Tag className="selected-audience-tag" key={audience.Id} role="listitem" size="small">{audience.Name}</Tag>
              )) }
            </TagGroup>
          </div>
        )
      }
    </>
  ), [CurrencyCode, accountSettings, bidOnlyMode, campaignSettings, i18n, onOpenSetupEditor, optOut, styles.additionalConversionValueContainer, styles.existingAudiencesContainer]);

  const renderEditorButton = useCallback(() => (
    <Button
      className={mergeClasses('edit-button', 'nca-create')}
      onClick={onOpenSetupEditor}
      disabled={optOut}
    >
      <Text size={200} weight="regular">
        {i18n.getString(_TL_('Set up new customer acquisition'))}
      </Text>
    </Button>
  ), [i18n, onOpenSetupEditor, optOut]);

  const renderBidHigher = useCallback(() => (
    <Radio
      className="bid-higher-option"
      value={NCA_STRATEGY_OPTIONS.BID_HIGHER}
      label={
        <div className={styles.radioOption}>
          <Text size={300} weight="regular">
            {i18n.getString(_TL_('Increase bids for new customers '))}
          </Text>
          { !bidOnlyMode && (
            <>
              { hasValidationError && <BiddingStrategyWarning style={styles.messageBar} i18n={i18n} /> }
              { !optOut && !accountSettings && <AccountLevelRequiredWarning style={styles.messageBar} i18n={i18n} /> }
              { accountSettings ? renderSettings() : renderEditorButton() }
            </>
          )}
        </div>
      }
    />
  ), [optOut, styles.radioOption, styles.messageBar, i18n, bidOnlyMode, hasValidationError, accountSettings, renderSettings, renderEditorButton]);

  const renderBidOnly = useCallback(() => (
    <Radio
      className="bid-only-option"
      value={NCA_STRATEGY_OPTIONS.BID_ONLY}
      label={
        <div className={styles.radioOption}>
          <Text size={300} weight="regular">
            {i18n.getString(_TL_('Bid only for new customers'))}
          </Text>
          { bidOnlyMode && (
            <>
              { !optOut && !accountSettings && <AccountLevelRequiredWarning style={styles.messageBar} i18n={i18n} /> }
              { accountSettings ? renderSettings() : renderEditorButton() }
            </>
          )}
        </div>
      }
    />
  ), [optOut, styles.radioOption, styles.messageBar, i18n, bidOnlyMode, accountSettings, renderSettings, renderEditorButton]);

  const renderEditor = useCallback(() => (
    <CustomerAcquisitionSetup
      newCustomerAcquisition={newCustomerAcquisition}
      isCampaignCreation
      isAccountLevel={!accountSettings}
      cid={cid}
      aid={aid}
      i18n={i18n}
      onChange={onChange}
      isEditorOpen={isSetupEditorOpen}
      onClose={onCloseSetupEditor}
    />
  ), [newCustomerAcquisition, accountSettings, cid, aid, i18n, onChange, isSetupEditorOpen, onCloseSetupEditor]);

  useEffect(() => {
    if (hasValidationError) {
      onChange({}, [i18n.getString(_TL_('To use this option, change bidding strategy to Maximize conversion value.'))]);
    } else {
      onChange({}, []);
    }
  }, [i18n, hasValidationError, onChange, newCustomerAcquisition]);

  return (
    <div className="nca-container">
      <Label className={styles.title} weight="semibold">{i18n.getString(_TL_('Optimization strategy'))}</Label>
      <RadioGroup
        className={mergeClasses(styles.radioGroupContainer, 'nca-option-container')}
        value={bidOnlyMode ? NCA_STRATEGY_OPTIONS.BID_ONLY : NCA_STRATEGY_OPTIONS.BID_HIGHER}
        onChange={onRadioChange}
        disabled={optOut}
      >
        {renderBidHigher()}
        {renderBidOnly()}
        {renderEditor()}
      </RadioGroup>
    </div>
  );
};

NewCustomerAcquisitionImpl.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    formatCurrency: PropTypes.func,
  }).isRequired,
  newCustomerAcquisition: PropTypes.shape({
    campaignSettings: PropTypes.shape({
      additionalConversionValue: PropTypes.number,
      bidOnlyMode: PropTypes.bool,
      goalId: PropTypes.number,
    }),
    accountSettings: PropTypes.shape({
      additionalConversionValue: PropTypes.number,
      audiences: PropTypes.arrayOf({
        Id: PropTypes.number,
        Name: PropTypes.string,
      }),
      goalId: PropTypes.number,
    }),
  }).isRequired,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  bidStrategy: PropTypes.string.isRequired,
  optOut: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};
